import {Errors} from "./errors"
import {parseSex, Sex} from "./user"
import {removeNonVisibleSymbols} from "./utils"

export class ValidationError extends Error {
    constructor(public error: Errors) {
        super();
    }
}

export class LoginValidator {
    static MIN_LENGTH = 3
    static MAX_LENGTH = 24

    static regexpEng = /.*[a-z]+.*/mi
    static regexpRu = /.*[а-я]+.*/mi
    static regexpGr = /.*[α-ωΑ-ΩίϊΐόάέύϋΰήώΊΪΌΆΈΎΫΉΏ]+.*/mi
    static regexpNonVisibleSymbols = /.*[᠌]+.*/mi
    static regexpSpace = /\s/mi

    static languagesRegexps = [LoginValidator.regexpEng, LoginValidator.regexpRu, LoginValidator.regexpGr]

    static validate(rawLogin: string, allowSpaces: boolean): string {
        if (!rawLogin) {
            throw new ValidationError(Errors.EMPTY_LOGIN)
        }

        let login = removeNonVisibleSymbols(rawLogin);

        if (!login || login.length < this.MIN_LENGTH) {
            throw new ValidationError(Errors.EMPTY_LOGIN)
        }

        if (login[0] === '.' || login[0] === '@') {
            throw new ValidationError(Errors.INCORRECT_SYMBOLS_IN_LOGIN)
        }

        if (LoginValidator.regexpNonVisibleSymbols.test(login)) {
            throw new ValidationError(Errors.OTHER_INCORRECT_SYMBOLS_IN_LOGIN)
        }

        if (login.length > this.MAX_LENGTH) {
            throw new ValidationError(Errors.LOGIN_TOO_LONG)
        }

        if(!allowSpaces && LoginValidator.regexpSpace.test(login)) {
            throw new ValidationError(Errors.SPACES_NOT_ALLOWED_IN_LOGIN)
        }

        let hasAnyLanguage = false
        for (let languageRegexp of LoginValidator.languagesRegexps) {
            const hasCurrentLanguage = languageRegexp.test(login)

            if(hasAnyLanguage && hasCurrentLanguage) {
                throw new ValidationError(Errors.SEVERAL_LANGUAGES_IN_LOGIN)
            }

            hasAnyLanguage = hasAnyLanguage || hasCurrentLanguage
        }

        return login
    }
}

export class SexValidator {
    static validate(rawSex: string): Sex {
        const sexEnum = parseSex(rawSex.trim())

        if (!sexEnum) {
            throw new ValidationError(Errors.UNKNOWN_SEX);
        }

        return sexEnum
    }
}

export class EmailValidator {
    static validate(rawEmail: string): string {
        if (!rawEmail) {
            throw new ValidationError(Errors.EMAIL_INCORRECT);
        }

        let email = rawEmail.trim()

        if (this.validateEmail(email)) {
            return email
        } else {
            throw new ValidationError(Errors.EMAIL_INCORRECT);
        }
    }

    private static validateEmail(email: string): boolean {
        //sources from library "email-validator": "^2.0.4",

        const tester = /^[-!#$%&'*+\/0-9=?A-Z^_a-z{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;
        // Thanks to:
        // http://fightingforalostcause.net/misc/2006/compare-email-regex.php
        // http://thedailywtf.com/Articles/Validating_Email_Addresses.aspx
        // http://stackoverflow.com/questions/201323/what-is-the-best-regular-expression-for-validating-email-addresses/201378#201378

        if (!email)
            return false;

        if (email.length > 254)
            return false;

        let valid = tester.test(email);
        if (!valid)
            return false;

        // Further checking of some things regex can't handle
        let parts = email.split("@");
        if (parts[0].length > 64)
            return false;

        let domainParts = parts[1].split(".");
        if (domainParts.some(function (part) {
            return part.length > 63;
        }))
            return false;

        return true;
    }
}

export class PasswordValidator {
    static MIN_LENGTH = 6
    static MAX_LENGTH = 24

    static validate(rawPassword: string): string {
        if (!rawPassword) {
            throw new ValidationError(Errors.PASSWORD_TOO_SHOR)
        }

        let password = removeNonVisibleSymbols(rawPassword);

        if (!password || password.length < this.MIN_LENGTH) {
            throw new ValidationError(Errors.PASSWORD_TOO_SHOR)
        }

        if (password.length > this.MAX_LENGTH) {
            throw new ValidationError(Errors.PASSWORD_TOO_LONG)
        }

        if (password.match(/^[0-9]+$/) != null) {
            throw new ValidationError(Errors.PASSWORD_ONLY_FROM_DIGITS)
        }

        return password
    }
}
